import type { TranslationLanguage, TafsirLanguage } from '@/types/domain';
import {
  TranslationPillGroup,
  TranslationPill,
} from '@/components/ui/TranslationPillGroup';

/**
 * Reader toolbar — unified pill cluster for reading preferences.
 *
 * Layout (driven by the design sketch):
 *   Row 1: EN  ·  UR  ·  BOTH  ·  TR
 *      - Translation language is one of 3 radios (no Arabic-only).
 *      - TR is a self-contained switch (no separate dot).
 *   Row 2: TAFSIR /EN  ·  TAFSIR /UR
 *      - Two combined pills replacing the old on/off + language cluster.
 *      - Clicking either one turns Tafsir ON in that language.
 *      - Clicking the currently-active one turns Tafsir OFF.
 *      - If you click the inactive one while the other is on, the
 *        language switches with no off blink.
 *
 * `translation_language: 'none'` is still a valid DB value (existing
 * users may have it). We just don't expose a pill to set it any more —
 * if they want pure Arabic they can flip to 'en' and choose not to
 * read the translation, or reach into Settings later.
 */

const LANGUAGE_OPTIONS: { value: TranslationLanguage; label: string }[] = [
  { value: 'en', label: 'EN' },
  { value: 'ur', label: 'UR' },
  { value: 'both', label: 'BOTH' },
];

type Props = {
  // Translation language radios (EN / UR / BOTH)
  translationLanguage: TranslationLanguage;
  onTranslationLanguageChange: (next: TranslationLanguage) => void;

  // Transliteration switch (TR)
  showTransliteration: boolean;
  onShowTransliterationChange: (next: boolean) => void;

  // Tafsir cluster — optional. When `showTafsirControls` is false the
  // second row is hidden entirely (used on /memorize, etc.).
  showTafsirControls?: boolean;
  showTafsir?: boolean;
  onShowTafsirChange?: (next: boolean) => void;
  tafsirLanguage?: TafsirLanguage;
  onTafsirLanguageChange?: (next: TafsirLanguage) => void;
};

export function ReaderToolbar({
  translationLanguage,
  onTranslationLanguageChange,
  showTransliteration,
  onShowTransliterationChange,
  showTafsirControls = false,
  showTafsir = false,
  onShowTafsirChange,
  tafsirLanguage = 'en',
  onTafsirLanguageChange,
}: Props) {
  // Handle the combined Tafsir pill behavior:
  //   - click active pill → turn OFF
  //   - click inactive pill → turn ON + set language
  function handleTafsirPillClick(lang: TafsirLanguage) {
    const isCurrentlyThisLang = showTafsir && tafsirLanguage === lang;
    if (isCurrentlyThisLang) {
      onShowTafsirChange?.(false);
      return;
    }
    // Set language first (when supported), then turn on. Order matters
    // for consumers that gate language updates on showTafsir.
    onTafsirLanguageChange?.(lang);
    if (!showTafsir) {
      onShowTafsirChange?.(true);
    }
  }

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Row 1 — translation language + transliteration */}
      <TranslationPillGroup ariaLabel="Translation language and transliteration">
        {LANGUAGE_OPTIONS.map((opt) => (
          <TranslationPill
            key={opt.value}
            active={opt.value === translationLanguage}
            onClick={() => onTranslationLanguageChange(opt.value)}
          >
            {opt.label}
          </TranslationPill>
        ))}

        <TranslationPill
          role="switch"
          active={showTransliteration}
          ariaLabel="Transliteration"
          onClick={() => onShowTransliterationChange(!showTransliteration)}
        >
          TR
        </TranslationPill>
      </TranslationPillGroup>

      {/* Row 2 — combined Tafsir pills (only when consumer opted in) */}
      {showTafsirControls && (
        <TranslationPillGroup ariaLabel="Tafsir">
          <TranslationPill
            role="switch"
            active={showTafsir && tafsirLanguage === 'en'}
            ariaLabel="Tafsir English"
            onClick={() => handleTafsirPillClick('en')}
          >
            TAFSIR /EN
          </TranslationPill>
          <TranslationPill
            role="switch"
            active={showTafsir && tafsirLanguage === 'ur'}
            ariaLabel="Tafsir Urdu"
            onClick={() => handleTafsirPillClick('ur')}
          >
            TAFSIR /UR
          </TranslationPill>
        </TranslationPillGroup>
      )}
    </div>
  );
}
